import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { Flask, Warning } from '@phosphor-icons/react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const GR10_LABEL = 'pro3dure audioprint GR-10 clear-transparent — REF A1000800';

// Action dédiée de changement de lot : lot précédent affiché, nouveau n° saisi, confirmation de nettoyage obligatoire.
export default function ChangeLotDialog({ tank, open, onOpenChange, onChanged }) {
  const [lotNumber, setLotNumber] = useState('');
  const [supplier, setSupplier] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cleaned, setCleaned] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => { if (open) { setLotNumber(''); setSupplier(''); setExpiry(''); setCleaned(false); setError(null); } }, [open, tank?.id]);

  const canSubmit = !!tank && lotNumber.trim().length > 0 && cleaned && !loading;

  const submit = async () => {
    if (!tank) return;
    if (!cleaned) { setError('Confirmation de nettoyage obligatoire'); return; }
    setLoading(true); setError(null);
    try {
      const r = await axios.post(`${API}/resin-tanks/${tank.id}/change-lot`, { lot_number: lotNumber.trim(), cleaning_confirmed: cleaned, supplier: supplier || null, expiry_date: expiry || null });
      toast.success(`Lot ${r.data.active_lot_number} activé sur ${r.data.name}`);
      onChanged?.(r.data);
      onOpenChange(false);
    } catch (e) {
      setError(e.response?.data?.detail || 'Erreur lors du changement de lot');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white max-w-[92vw] sm:max-w-md" data-testid="change-lot-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Flask size={18} />Changement de lot — {tank?.name}</DialogTitle>
          <DialogDescription>{GR10_LABEL}{tank?.machine ? ` · Machine : ${tank.machine}` : ''}</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="p-3 bg-zinc-50 border border-zinc-200 rounded-sm" data-testid="previous-lot">
            <p className="text-[10px] uppercase tracking-wider text-zinc-400">Lot précédent (sera clôturé)</p>
            <p className="font-mono font-semibold text-zinc-900">{tank?.active_lot_number || <span className="italic font-sans font-normal text-zinc-400">Aucun lot actif</span>}</p>
            {tank?.active_lot_since && <p className="text-xs text-zinc-500">Actif depuis le {new Date(tank.active_lot_since).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })}{tank.active_lot_set_by_name ? ` · ${tank.active_lot_set_by_name}` : ''}</p>}
          </div>
          <div>
            <Label className="text-xs">Nouveau numéro de lot *</Label>
            <Input value={lotNumber} onChange={(e) => setLotNumber(e.target.value)} placeholder="ex. GR10-2026-0042" className="rounded-sm font-mono mt-1" autoFocus data-testid="new-lot-number-input" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Fournisseur (optionnel)</Label>
              <Input value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="pro3dure" className="rounded-sm mt-1" data-testid="new-lot-supplier-input" />
            </div>
            <div>
              <Label className="text-xs">Péremption (optionnel)</Label>
              <Input type="date" value={expiry} onChange={(e) => setExpiry(e.target.value)} className="rounded-sm mt-1" data-testid="new-lot-expiry-input" />
            </div>
          </div>
          <label className={`flex items-start gap-3 p-3 border rounded-sm cursor-pointer ${cleaned ? 'border-emerald-400 bg-emerald-50' : 'border-amber-300 bg-amber-50'}`} data-testid="cleaning-confirm-label">
            <Checkbox checked={cleaned} onCheckedChange={(v) => setCleaned(!!v)} className="mt-0.5" data-testid="cleaning-confirm-checkbox" />
            <span className="text-sm text-zinc-800">Je confirme que le bac a été <strong>vidé et nettoyé conformément à la procédure</strong> avant l'activation du nouveau lot.</span>
          </label>
          <p className="text-[11px] text-zinc-500">Le changement est tracé (opérateur, date, heure). Les dispositifs déjà démarrés conservent leur lot d'origine. Aucun remplacement du FEP n'est imposé par cette action.</p>
          {error && <p className="flex items-center gap-1 text-sm text-red-700" data-testid="change-lot-error"><Warning size={14} />{error}</p>}
        </div>
        <DialogFooter className="flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="change-lot-cancel">Annuler</Button>
          <Button onClick={submit} disabled={!canSubmit} className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white" data-testid="change-lot-submit">{loading ? 'Activation…' : 'Activer le nouveau lot'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
